import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useCompany } from '@/contexts/useCompany';
import { describePersistError } from '@/lib/persistErrors';
import { supabase } from '@/integrations/supabase/client';
import { SignUpModal } from '@/components/modals/SignUpModal';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { QuoteForm, QuoteFormData, QuoteItem, calculateTotal } from '@/components/quotes/QuoteForm';
import { QuoteActionBar, ActionSpinner } from '@/components/quotes/QuoteActionBar';
import {
  buildQuoteTotalsColumns,
  computeQuoteTotals,
  quoteHeaderFromRow,
  quoteItemLineTotal,
} from '@/lib/quoteTotals';
import { buildQuotePaymentColumns, defaultPaymentMethodsForNewQuote, quotePaymentInfoFromRow } from '@/lib/quotePaymentInfo';
import { buildQuotePdfTemplateColumns, quotePdfTemplateFromRow, resolveQuotePdfTemplate } from '@/lib/quotePdfTemplate';
import { buildQuoteWarrantyColumns, quoteWarrantyFromRow, warrantyDisplayText } from '@/lib/quoteWarranty';
import { buildQuoteHeadingColumns, quoteHeadingFromRow } from '@/lib/quoteHeading';
import { buildQuoteExtraFieldsColumns, quoteExtraFieldsFromRow } from '@/lib/quoteExtraFields';
import type { GuestQuote } from '@/types/database';
import { 
  ArrowLeft, 
  FileDown, 
  Loader2, 
  Lock, 
  Edit, 
  Save,
  Info,
  Link,
  Eye,
  WifiOff,
  Hash,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadQuotePDF, PDFQuoteData } from '@/lib/pdfGenerator';
import { getPublicQuoteUrl } from '@/lib/urls';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { cacheCatalog, getCachedCatalog, getCachedQuotes } from '@/lib/offlineDataCache';
import { ensureStorageUrl } from '@/lib/offlineSync/storageUpload';
import { persistQuoteOffline as persistQuoteOfflineLib } from '@/lib/offlineSync/persistQuoteOffline';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { createUuid } from '@/lib/uuid';
import { onlyDigits } from '@/lib/documentUtils';
import { buildNewClientColumns, resolveClientAddressLine } from '@/lib/clientAddress';
import {
  buildQuoteItemInsert,
  catalogProductColumnsFromQuoteItem,
  quoteItemUnitCost,
} from '@/lib/quoteItems';
import { createCatalogProductFromQuoteItem } from '@/lib/catalogFromQuoteItem';
import { QUOTE_STATUS_OPTIONS } from '@/lib/quoteStatus';
import { plural } from '@/lib/plural';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { releaseSavedUploads } from '@/lib/pendingUploads';
import {
  documentTypeOfLabel,
  getDocumentTypeShortLabel,
} from '@/lib/documentTypeLabels';
import { cn } from '@/lib/utils';

const GUEST_QUOTE_KEY = 'orcazap_guest_quote';
const DRAFT_KEY = 'orcazap_draft_new';

/**
 * A "impressão digital" do formulário, usada para decidir se há trabalho não
 * salvo (o que liga o autossave do rascunho e o indicador "Continuar orçamento").
 *
 * ⚠️ ERA UMA LISTA EXPLÍCITA DE SEIS CAMPOS, ESCRITA TRÊS VEZES — e campo fora
 * dela é trabalho que o usuário PERDE em silêncio: o rascunho nunca salva e o
 * indicador nunca aparece. Achado no aparelho ao preencher só o subtítulo e os
 * campos extras: nada era gravado, sem erro nenhum. A varredura mostrou que o
 * buraco era maior e anterior — `shipping`, `discount`, `paymentMethods`,
 * `paymentTerms`, `reference` e `pdfTemplate` também estavam de fora.
 *
 * Serializar o objeto INTEIRO resolve por construção: campo novo entra sozinho,
 * e é a diferença entre isto e a lista que voltaria a envelhecer (#33/#62).
 * `selectedClient` sai porque é o objeto do cliente escolhido — ele muda de
 * identidade a cada refetch da lista e marcaria o formulário como sujo sozinho.
 */
const dirtyFingerprint = (data: QuoteFormData): string => {
  const { selectedClient, ...resto } = data;
  return JSON.stringify({ ...resto, selectedClientId: selectedClient?.id ?? null });
};

const getEmptyFormData = (): QuoteFormData => ({
  clientName: '',
  selectedClient: null,
  quoteDate: new Date().toISOString().split('T')[0],
  status: 'pending',
  documentType: 'orcamento',
  validityDays: '7',
  warranty: '',
  warrantyAmount: null,
  warrantyUnit: null,
  notes: '',
  termsConditions: '',
  items: [],
  newClientPhone: '',
  newClientAddress: '',
  newClientDocument: '',
  saveNewClient: false,
  title: '',
  showSequenceId: false,
  generalPhotos: [],
});

export default function NewQuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const { canCreateQuote, incrementQuoteCount, setVisitorQuoteUsed } = useSubscription();
  const unlimitedQuotes = useFeature('unlimited_quotes');
  const noBrandingFooter = useFeature('no_branding_footer');
  const { activeCompany } = useCompany();
  // Viewers cannot create quotes. Guests (no user) have role null -> owner
  // default, so this only blocks logged-in read-only members.
  const { canWrite } = useRole();
  // Decide QR estático × dinâmico. Para visitante (sem sessão) resolve como
  // estático, que é o comportamento de sempre.
  const paymentDetection = usePaymentDetection();
  const { isOnline, checkOnline, saveQuoteOffline, saveClientOffline, saveCatalogItemOffline } = useOfflineSync();

  const isViewMode = searchParams.get('view') === 'true';
  const isGuestReadOnly = !user && isViewMode;

  // Read-only members (viewers) cannot create quotes — bounce them out even if
  // they reach /quotes/new directly by URL. Guests keep the visitor flow.
  useEffect(() => {
    if (user && !canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      navigate('/quotes');
    }
  }, [user, canWrite, navigate]);

  // Draft restoration state
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [nextSequenceId, setNextSequenceId] = useState<number | null>(null);
  const [savedQuoteId, setSavedQuoteId] = useState<string | null>(null);
  // Locked sequence ID after save - prevents display from changing to next predicted number
  const [savedSequenceId, setSavedSequenceId] = useState<number | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track initial form data to detect dirty state
  const initialFormDataRef = useRef<string>('');
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Form state - initialized from draft if available
  const [formData, setFormDataInternal] = useState<QuoteFormData>(() => {
    // Don't restore draft in view mode
    if (isViewMode) return getEmptyFormData();
    
    try {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        // Check if it has meaningful data
        if (parsed?.clientName || (parsed?.items && parsed.items.length > 0)) {
          return { ...getEmptyFormData(), ...parsed };
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
    return getEmptyFormData();
  });

  // Ref to always hold the latest formData (avoids stale closure in handleSave)
  const formDataRef = useRef<QuoteFormData>(formData);

  // Wrapper that updates both state and ref synchronously
  const setFormData = useCallback((updater: React.SetStateAction<QuoteFormData>) => {
    setFormDataInternal((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      formDataRef.current = next; // Sync ref immediately
      return next;
    });
  }, []);
  
  // Store initial form data on mount to detect dirty state
  useEffect(() => {
    if (isViewMode) return;
    
    // Check if we restored from draft on mount FIRST
    try {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        if (parsed?.clientName || (parsed?.items && parsed.items.length > 0)) {
          setRestoredFromDraft(true);
          toast.success('Restauramos seu rascunho não salvo.');
          // Set initial state AFTER restoring draft, so we compare against the restored data
          // This prevents immediately marking as dirty after restoration
          initialFormDataRef.current = dirtyFingerprint({
            ...getEmptyFormData(),
            ...parsed,
          });
          // Auto-hide the restored message after 5 seconds
          setTimeout(() => setRestoredFromDraft(false), 5000);
          return; // Skip setting empty data as initial
        }
      }
    } catch (error) {
      console.error('Error checking draft:', error);
    }
    
    // Set initial state only once (on mount) - only if no draft was restored
    if (!initialFormDataRef.current) {
      const emptyData = getEmptyFormData();
      initialFormDataRef.current = dirtyFingerprint(emptyData);
    }
  }, [isViewMode]);

  // Fetch next sequence ID for preview (only if not already saved)
  // Fetch next sequence ID from profile's lifetime_quotes_created (persistent, never decrements)
  useEffect(() => {
    const fetchNextSequenceId = async () => {
      // Don't fetch if already saved - we want to keep the locked sequence ID
      if (!user || isViewMode || savedSequenceId !== null) return;
      
      try {
        // Use lifetime_quotes_created from profile as the base for sequence ID
        // This ensures the number never regresses even if quotes are deleted
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('lifetime_quotes_created')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching sequence ID:', error);
          return;
        }

        // Next sequence ID = lifetime_quotes_created + 1
        const currentCount = profileData?.lifetime_quotes_created ?? 0;
        const nextId = currentCount + 1;
        setNextSequenceId(nextId);
        
        // Update formData with the sequence ID
        setFormData(prev => ({ ...prev, sequenceId: nextId }));
      } catch (error) {
        console.error('Error fetching sequence ID:', error);
      }
    };

    fetchNextSequenceId();
  }, [user, isViewMode, setFormData, savedSequenceId]);

  // Check dirty state whenever formData changes
  useEffect(() => {
    if (isViewMode || isGuestReadOnly) return;
    // Don't check dirty if already saved (we already cleared the draft)
    if (savedQuoteId) return;
    
    const currentFormData = dirtyFingerprint(formData);
    
    const isDirty = currentFormData !== initialFormDataRef.current;
    setIsFormDirty(isDirty);
  }, [formData, isViewMode, isGuestReadOnly, savedQuoteId]);

  // Pre-fill default terms from active company (only for new quotes without draft)
  useEffect(() => {
    if (isViewMode || isGuestReadOnly || restoredFromDraft) return;
    if (!activeCompany?.default_terms) return;
    // Only pre-fill if terms are currently empty
    if (formData.termsConditions) return;
    
    setFormData(prev => ({
      ...prev,
      termsConditions: activeCompany.default_terms || '',
    }));
  }, [activeCompany?.default_terms, isViewMode, isGuestReadOnly, restoredFromDraft, setFormData]);

  /**
   * Pré-seleciona as formas de pagamento aceitas, do mesmo jeito que os termos
   * padrão acima — é o que faz a transição desta feature ser invisível.
   *
   * O guard é `prev.paymentMethods !== null`: só preenche enquanto o campo
   * estiver em "não informado". Depois que o usuário toca no bloco (mesmo para
   * desmarcar tudo, o que grava `[]`) a escolha dele é definitiva, e um `[]`
   * NÃO pode ser confundido com "vazio, pode preencher" — senão desmarcar tudo
   * seria desfeito pelo próprio effect no render seguinte.
   */
  useEffect(() => {
    if (isViewMode || isGuestReadOnly || restoredFromDraft) return;
    if (!activeCompany) return;
    const prefill = defaultPaymentMethodsForNewQuote(activeCompany);
    if (!prefill) return;

    setFormData(prev => (prev.paymentMethods === null || prev.paymentMethods === undefined
      ? { ...prev, paymentMethods: prefill }
      : prev));
  }, [activeCompany, isViewMode, isGuestReadOnly, restoredFromDraft, setFormData]);

  /**
   * Pré-preenche o LOCAL DE EMISSÃO com a cidade da empresa — a mesma fonte que
   * o Recibo já usa (`companies.pix_city`, o único campo de cidade estruturado
   * que existe; `address` é linha livre e não dá para extrair município dela).
   *
   * Só vale para documento NOVO: a hidratação de um orçamento salvo vem da
   * linha (`quoteHeadingFromRow`), então reabrir um documento antigo continua
   * com o campo vazio e o PDF dele sai idêntico ao que foi enviado. E o guard
   * `prev.issuePlace` respeita quem apagou a cidade de propósito.
   */
  useEffect(() => {
    if (isViewMode || isGuestReadOnly || restoredFromDraft) return;
    const city = (activeCompany as { pix_city?: string | null } | null)?.pix_city?.trim();
    if (!city) return;

    setFormData(prev => (prev.issuePlace ? prev : { ...prev, issuePlace: city }));
  }, [activeCompany, isViewMode, isGuestReadOnly, restoredFromDraft, setFormData]);

  // Auto-save draft with debounce - ONLY if form is dirty AND not yet saved
  useEffect(() => {
    // Don't save draft in view mode, for guest viewing, or if already saved
    if (isViewMode || isGuestReadOnly || savedQuoteId) return;
    
    // Only save if form is dirty (user has made changes)
    if (!isFormDirty) return;
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Save draft after 1 second of inactivity
    debounceTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
      } catch (error) {
        console.error('Error saving draft:', error);
      }
    }, 1000);
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formData, isViewMode, isGuestReadOnly, savedQuoteId, isFormDirty]);

  // Clear draft on successful save
  const clearDraft = () => {
    // O documento acabou de virar dono das fotos que ainda referencia; as que
    // sobraram (foto trocada, foto removida, item inteiro excluído antes do
    // save) não têm mais ninguém apontando para elas. Este é o único ponto do
    // fluxo de sucesso por onde TODOS os caminhos de save passam.
    void releaseSavedUploads(formDataRef.current);
    setFormData((prev) => (prev.pendingUploads?.length ? { ...prev, pendingUploads: [] } : prev));
    try {
      localStorage.removeItem(DRAFT_KEY);
      // Reset dirty state since we saved
      setIsFormDirty(false);
      // Dispatch event to hide DraftIndicator
      window.dispatchEvent(new StorageEvent('storage', { key: DRAFT_KEY }));
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  };

  // Update existing quote (instead of creating duplicate)
  const updateExistingQuote = async (data: QuoteFormData) => {
    if (!user || !savedQuoteId) return;
    
    setSaving(true);
    
    try {
      const clientName = data.clientName.trim();
      const clientPhone = data.newClientPhone?.trim() || null;
      const clientAddress = resolveClientAddressLine(data);
      const clientDocument = onlyDigits(data.newClientDocument || '') || null;
      const items: QuoteItem[] = Array.isArray(data.items) ? data.items : [];
      const finalTotal = calculateTotal({ ...data, items });

      // Update quote
      await supabase
        .from('quotes')
        .update({
          client_name: clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          status: data.status,
          document_type: data.documentType || 'orcamento',
          validity_days: parseInt(data.validityDays) || null,
          ...buildQuoteWarrantyColumns(data),
          ...buildQuoteHeadingColumns(data),
          ...buildQuoteExtraFieldsColumns(data),
          notes: data.notes || null,
          terms_conditions: data.termsConditions || null,
          // Frete, desconto de cabeçalho e o total que já os inclui vêm do
          // builder único — a lista explícita de colunas é a armadilha #33/#62.
          ...buildQuoteTotalsColumns({ ...data, items }),
          ...buildQuotePaymentColumns(data),
          ...buildQuotePdfTemplateColumns(data),
          title: data.title?.trim() || null,
          show_sequence_id: data.showSequenceId || false,
        })
        .eq('id', savedQuoteId);

      // Delete old items and insert new ones
      await supabase.from('quote_items').delete().eq('quote_id', savedQuoteId);

      // Builder único (src/lib/quoteItems.ts): preserva product_id e o SNAPSHOT
      // de unit_cost, e evita a lista de colunas copiada em cada save.
      const itemsToInsert = items.map((item, index) =>
        buildQuoteItemInsert(item, index, savedQuoteId),
      );

      await supabase.from('quote_items').insert(itemsToInsert);
      window.dispatchEvent(new CustomEvent('quotes-updated'));
    } finally {
      setSaving(false);
    }
  };
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  // Load guest quote data when in view mode
  useEffect(() => {
    if (isViewMode && !user) {
      const savedQuote = localStorage.getItem(GUEST_QUOTE_KEY);
      if (savedQuote) {
        try {
          const quote: GuestQuote = JSON.parse(savedQuote);
          setFormData({
            clientName: quote.client_name || '',
            selectedClient: null,
            quoteDate: new Date().toISOString().split('T')[0],
            status: quote.status || 'pending',
            documentType: quote.document_type || 'orcamento',
            validityDays: quote.validity_days?.toString() || '7',
            ...quoteWarrantyFromRow(quote),
            // Subtitulo + local de emissao. Mesma armadilha #70 dos campos acima:
            // perder a LEITURA faz o proximo save gravar vazio por cima e o
            // cabecalho do documento voltar sozinho ao que era.
            ...quoteHeadingFromRow(quote),
            extraFields: quoteExtraFieldsFromRow(quote),
            notes: quote.notes || '',
            termsConditions: quote.terms_conditions || '',
            title: quote.title || '',
            // Frete e desconto de CABEÇALHO. Sem estas três linhas o orçamento
            // do visitante reabre valendo só a soma dos itens: o frete que ele
            // digitou some da tela e o Total exibido passa a divergir do que
            // está gravado (e do PDF que ele já baixou). Achado no teste ao
            // vivo — o guard de varredura só cobre os payloads de ESCRITA.
            ...quoteHeaderFromRow(quote),
            // Mesmo motivo, para os campos do grupo 4: sem esta linha o
            // visitante reabre o rascunho com as formas de pagamento em branco
            // e o QR PIX de volta — exatamente o buraco do frete (armadilha
            // #70), que o guard de ESCRITA não pega.
            ...quotePaymentInfoFromRow(quote),
            // Rascunho de visitante não guarda modelo (o seletor é PRO) — a
            // leitura devolve null e o documento segue o padrão. A linha existe
            // pela simetria leitura↔escrita do guard (#70).
            pdfTemplate: quotePdfTemplateFromRow(quote as { pdf_template?: string | null }),
            items: quote.items?.map((item) => ({
              id: item.id || createUuid(),
              name: item.name || '',
              description: item.description || '',
              quantity: item.quantity || 1,
              unit_price: item.unit_price || 0,
              discount: item.discount || 0,
              discount_type: item.discount_type || 'value',
              image_url: item.image_url || undefined,
              source: 'manual' as const,
              itemType: item.itemType || 'service',
              unit: item.unit || '',
            })) || [],
          });
        } catch (e) {
          console.error('Error loading guest quote:', e);
        }
      }
    }
  }, [isViewMode, user]);

  const total = calculateTotal(formData);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Helper function to convert image URL to Base64 (for CORS/async loading)
  const getDataUrl = async (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } else {
            resolve(null);
          }
        } catch (e) {
          console.error('Error converting image to base64:', e);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.error('Error loading image:', url);
        resolve(null);
      };
      img.src = url;
    });
  };

  const generatePDF = async () => {
    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (result) {
        return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
      }
      return [16, 185, 129]; // Default emerald
    };

    // Get client data (from selectedClient or new client fields)
    const clientPhone = formData.selectedClient?.phone || formData.newClientPhone;
    const clientAddress = formData.selectedClient?.address || formData.newClientAddress;
    const clientDocument = onlyDigits(formData.selectedClient?.document || formData.newClientDocument || '') || null;

    // Build PDF data using the unified generator
    const pdfData: PDFQuoteData = {
      documentType: (formData.documentType || 'orcamento') as PDFQuoteData['documentType'],
      // O título do prestador SUBSTITUI a palavra do tipo no cabeçalho do PDF
      // (`resolveDocumentTitle`). Vazio cai no rótulo de sempre, então o
      // documento de quem não titula sai byte a byte igual ao histórico.
      title: formData.title,
      // Subtitulo + local de emissao: sem eles o PDF nao teria por onde
      // saber o cabecalho que o usuario montou (armadilha #70, agora do
      // lado do PAYLOAD e nao da hidratacao).
      subtitle: formData.subtitle,
      issuePlace: formData.issuePlace,
      extraFields: formData.extraFields,
      client: {
        name: formData.clientName || 'Não informado',
        phone: clientPhone || undefined,
        address: clientAddress || undefined,
        document: clientDocument || undefined,
      },
      company: activeCompany ? {
        name: activeCompany.name || 'Minha Empresa',
        cnpj: activeCompany.cnpj_cpf || undefined,
        address: activeCompany.address || undefined,
        phone: activeCompany.phone || undefined,
        email: activeCompany.email || undefined,
        logoUrl: activeCompany.logo_url || undefined,
        pixKey: activeCompany.pix_key || undefined,
        pixCity: activeCompany.pix_city || undefined,
      } : undefined,
      quoteDate: formData.quoteDate,
      validityDays: formData.validityDays ? parseInt(formData.validityDays) : undefined,
      warranty: warrantyDisplayText(formData) || undefined,
      notes: formData.notes || undefined,
      termsConditions: formData.termsConditions || undefined,
      items: formData.items.map(item => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount || 0,
        discount_type: item.discount_type || 'value',
        unit: item.unit,
        image_url: item.image_url,
      })),
      total,
      // Frete e desconto de CABEÇALHO: sem eles o PDF imprimiria só a linha de
      // total, sem a decomposição, e o cliente não veria de onde veio o número.
      shipping: formData.shipping ?? 0,
      discount: formData.discount ?? 0,
      discountType: formData.discountType || 'value',
      // Campos de EXIBIÇÃO do grupo 4. `paymentMethods` precisa chegar ao PDF
      // PRESERVANDO o `null`: é ele que mantém o QR PIX nos documentos
      // anteriores a esta feature (ver `src/lib/quotePaymentInfo.ts`).
      paymentMethods: formData.paymentMethods ?? null,
      paymentTerms: formData.paymentTerms || null,
      reference: formData.reference || null,
      // Um orçamento que ainda não foi salvo não tem cobrança — então, com a
      // detecção ligada, este PDF sai SEM QR de propósito. Cair no QR estático
      // aqui seria pior: o cliente pagaria direto na chave pessoal, o app nunca
      // saberia, e a tela continuaria prometendo a baixa automática (#9). O QR
      // aparece depois de salvar e gerar a cobrança, na tela do orçamento.
      pixQrMode: paymentDetection.resolution.mode,
      pixDynamic: null,
      isPremium: noBrandingFooter.allowed,
      brandColorRGB: activeCompany ? hexToRgb(activeCompany.primary_color) : [16, 185, 129],
      // O escolhido NESTE documento, senão o padrão da empresa (o dispatcher
      // ainda força 'classico' para conta grátis — regra de plano fica lá).
      template: resolveQuotePdfTemplate(
        formData.pdfTemplate,
        (activeCompany as { pdf_template?: string | null } | null)?.pdf_template,
      ),
      sequenceId: formData.sequenceId,
      showSequenceId: formData.showSequenceId,
      generalPhotos: formData.generalPhotos?.map(p => ({
        imageUrl: p.imageUrl,
        title: p.title,
        description: p.description,
      })),
      // Assinatura salva do prestador (PRO). Um orçamento novo ainda não foi
      // aprovado, então não há assinatura do cliente para carimbar.
      providerSignatureDataUrl: activeCompany?.signature_url || undefined,
      // Idem QuoteDetailPage: só vale para `pedido`, mas o formulário permite
      // escolher esse tipo e os dois caminhos têm que concordar.
      pdvSignatureLine: activeCompany?.pdv_signature_line ?? true,
      // Marca-d'agua da logo (config da empresa). `=== true` e nao `Boolean`:
      // ausente/indefinida cai no padrao DESLIGADO, que e o historico.
      logoWatermark: activeCompany?.logo_watermark === true,
    };

    const filename = `orcamento-${formData.clientName || 'sem-nome'}-${Date.now()}.pdf`;
    await downloadQuotePDF(pdfData, filename);
  };

  // Thin wrapper binding the shared offline-persist helper to this component's
  // user/company and offline-save callbacks. Both offline paths ("Salvar" and
  // "Baixar PDF") go through it, so they persist an identical payload.
  const persistQuoteOffline = (data: QuoteFormData, opts?: { silent?: boolean }): string =>
    persistQuoteOfflineLib(
      data,
      {
        userId: user!.id,
        companyId: activeCompany?.id ?? null,
        saveClientOffline,
        saveCatalogItemOffline,
        saveQuoteOffline,
        // This is a normal quote (never PDV). The server-side RPC independently
        // exempts PRO profiles from the monthly counter.
        countTowardQuoteQuota: true,
      },
      opts,
    );

  const handleDownloadPDF = async () => {
    // Snapshot usando REF (sempre contém o estado mais recente)
    const currentData = formDataRef.current;
    
    // Validate before saving
    if (!currentData.clientName.trim()) {
      toast.warning('Informe o nome do cliente');
      return;
    }
    if (currentData.items.length === 0) {
      toast.warning('Adicione pelo menos um item ao orçamento');
      return;
    }
    if (currentData.items.some((item) => !item.name.trim())) {
      toast.warning('Preencha o nome de todos os itens');
      return;
    }

    // For logged-in users, save first then generate PDF
    if (user) {
      // Offline: full parity with the online create — persist the quote (to the
      // offline sync queue, since we can't reach the server) AND generate the
      // PDF, whose images are embedded from the offline byte cache (IndexedDB)
      // via resolveMediaUrl.
      if (!isOnline) {
        // Already-persisted quote (saved earlier): just regenerate the PDF.
        if (savedQuoteId) {
          try {
            setSaving(true);
            await generatePDF();
            toast.success('PDF gerado offline!', { icon: '📴' });
          } catch (e) {
            console.error('[NewQuotePage] Offline PDF error:', e);
            toast.error('Erro ao gerar o PDF offline.');
          } finally {
            setSaving(false);
          }
          return;
        }

        // New quote: honor the same subscription gate as the online create.
        if (!canCreateQuote && !unlimitedQuotes.allowed) {
          unlimitedQuotes.require();
          return;
        }

        try {
          setSaving(true);
          // Queue the quote for sync (silent: single combined toast below), then
          // generate the PDF, then land on the (offline) quote detail like online.
          const offlineId = persistQuoteOffline(
            JSON.parse(JSON.stringify(currentData)),
            { silent: true },
          );
          await incrementQuoteCount({
            offline: true,
            countTowardQuota: !unlimitedQuotes.allowed,
          });
          clearDraft();
          await generatePDF();
          toast.success(
            'Orçamento salvo offline e PDF gerado! Será sincronizado quando houver internet.',
            { icon: '📴' },
          );
          navigate(`/quotes/${offlineId}`, { replace: true });
        } catch (e) {
          console.error('[NewQuotePage] Offline save+PDF error:', e);
          toast.error('Erro ao gerar o PDF offline.');
          setSaving(false);
        }
        return;
      }

      // If already saved, just regenerate PDF (no duplicate insert)
      if (savedQuoteId) {
        await updateExistingQuote(currentData);
        await generatePDF();
        toast.success('Orçamento atualizado e PDF gerado!');
        // Redirect to the edit page ("Editar orçamento")
        navigate(`/quotes/${savedQuoteId}`, { replace: true });
        return;
      }

      // Check subscription limit for new quotes only
      if (!canCreateQuote && !unlimitedQuotes.allowed) {
        unlimitedQuotes.require();
        return;
      }

      setSaving(true);

      // Salvar cliente na lista se marcado
      let finalClientId: string | null = currentData.selectedClient?.id || null;
      const clientName = currentData.clientName.trim();
      const clientPhone = currentData.newClientPhone?.trim() || null;
      const clientAddress = resolveClientAddressLine(currentData);
      const clientDocument = onlyDigits(currentData.newClientDocument || '') || null;

      if (currentData.saveNewClient && !currentData.selectedClient && clientName) {
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            user_id: user.id,
            company_id: activeCompany?.id ?? null,
            // Builder ÚNICO das colunas de `clients` (src/lib/clientAddress.ts).
            // São TRÊS caminhos que inserem este mesmo cliente — os dois saves
            // online desta tela e a fila offline — e montar a lista de colunas em
            // cada um é a armadilha #33/#62: o campo novo entra em dois e "some"
            // no terceiro, com toast de sucesso e sem erro nenhum.
            ...buildNewClientColumns(currentData),
          })
          .select('id')
          .maybeSingle();

        if (!clientError && newClient) {
          finalClientId = newClient.id;
        }
      }

      // Get current lifetime_quotes_created from profile for persistent sequence ID
      const { data: profileData } = await supabase
        .from('profiles')
        .select('lifetime_quotes_created')
        .eq('id', user.id)
        .maybeSingle();

      // Next sequence ID = lifetime_quotes_created + 1 (before incrementing)
      const currentCount = profileData?.lifetime_quotes_created ?? 0;
      const nextSequenceId = currentCount + 1;

      // Create new quote
      const { data: newQuote, error: quoteError } = await supabase
        .from('quotes')
        .insert({
          user_id: user.id,
          client_id: finalClientId,
          client_name: currentData.clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          status: currentData.status,
          document_type: currentData.documentType || 'orcamento',
          validity_days: parseInt(currentData.validityDays) || null,
          ...buildQuoteWarrantyColumns(currentData),
          ...buildQuoteHeadingColumns(currentData),
          ...buildQuoteExtraFieldsColumns(currentData),
          notes: currentData.notes || null,
          terms_conditions: currentData.termsConditions || null,
          ...buildQuoteTotalsColumns(currentData),
          ...buildQuotePaymentColumns(currentData),
          ...buildQuotePdfTemplateColumns(currentData),
          title: currentData.title?.trim() || null,
          sequence_id: nextSequenceId,
          show_sequence_id: currentData.showSequenceId || false,
          company_id: activeCompany?.id ?? null,
        })
        .select()
        .single();

      if (quoteError || !newQuote) {
        setSaving(false);
        toast.error('Erro ao salvar orçamento');
        return;
      }

      // Store the saved quote ID to enable action buttons
      setSavedQuoteId(newQuote.id);
      
      // Lock the sequence ID to prevent display from changing to next number
      setSavedSequenceId(nextSequenceId);
      setFormData(prev => ({ ...prev, sequenceId: nextSequenceId }));

      // Insert items with all fields (builder único — ver src/lib/quoteItems.ts)
      const itemsToInsert = currentData.items.map((item, index) =>
        buildQuoteItemInsert(item, index, newQuote.id),
      );

      await supabase.from('quote_items').insert(itemsToInsert);

      // Insert general photos if any
      const generalPhotos = currentData.generalPhotos || [];
      if (generalPhotos.length > 0) {
        const photosToInsert = await Promise.all(
          generalPhotos.map(async (photo, index) => ({
            quote_id: newQuote.id,
            user_id: user.id,
            image_url: await ensureStorageUrl(photo.imageUrl, 'quote-photos', 'general', user.id),
            title: photo.title || null,
            description: photo.description || null,
            position: index,
          }))
        );

        const { error: photosError } = await supabase.from('quote_photos').insert(photosToInsert);
        if (photosError) {
          console.error('Error saving general photos:', photosError);
          throw new Error(`Erro ao salvar fotos do orçamento: ${photosError.message}`);
        }
      }

      // Save items to catalog if checkbox is checked AND update quote_items with product_id
      const itemsToSaveToCatalog = currentData.items.filter(item => item.saveToCategory && item.name.trim());
      let catalogItemsSaved = 0;
      
      if (itemsToSaveToCatalog.length > 0) {
        // Porta ÚNICA: o produto, as fotos, as variações e os materiais saem de
        // `createCatalogProductFromQuoteItem`. Antes cada caminho montava a
        // própria lista de colunas e as listas divergiam — ver o cabeçalho do
        // módulo (armadilha #33/#62).
        for (const item of itemsToSaveToCatalog) {
          const outcome = await createCatalogProductFromQuoteItem({
            item,
            userId: user.id,
            companyId: activeCompany?.id ?? null,
          });

          if (!outcome.product) {
            toast.warning(`"${item.name}" não foi salvo no catálogo: ${outcome.error}`);
            continue;
          }

          // A linha aponta para a PRIMEIRA VARIAÇÃO quando há variações: o
          // agrupador não é vendável nem estocável (#30).
          await supabase
            .from('quote_items')
            .update({ product_id: outcome.product.linkProductId })
            .eq('quote_id', newQuote.id)
            .eq('name', item.name)
            .eq('unit_price', item.unit_price);

          catalogItemsSaved++;
        }
      }

      setSaving(false);
      window.dispatchEvent(new CustomEvent('quotes-updated'));
      await incrementQuoteCount({
        countTowardQuota: !unlimitedQuotes.allowed,
        sequenceId: nextSequenceId,
      });

      // Clear the draft after successful save
      clearDraft();

      // Generate PDF after successful save
      await generatePDF();

      if (catalogItemsSaved > 0) {
        toast.success(`Orçamento salvo, PDF gerado e ${catalogItemsSaved} ${catalogItemsSaved === 1 ? 'novo item adicionado' : 'novos itens adicionados'} ao catálogo!`);
      } else {
        toast.success('Orçamento salvo e PDF gerado com sucesso!');
      }

      // Redirect to the edit page so the user can continue editing normally
      navigate(`/quotes/${newQuote.id}`, { replace: true });
    } else {
      // For guest users, save to localStorage and generate PDF
      const guestQuote: GuestQuote = {
        id: createUuid(),
        client_name: currentData.clientName,
        status: currentData.status || 'pending',
        document_type: currentData.documentType || 'orcamento',
        validity_days: parseInt(currentData.validityDays) || null,
        ...buildQuoteWarrantyColumns(currentData),
        ...buildQuoteHeadingColumns(currentData),
        ...buildQuoteExtraFieldsColumns(currentData),
        notes: currentData.notes || null,
        terms_conditions: currentData.termsConditions || null,
        // O rascunho do visitante guarda frete/desconto para que a importação
        // na conta nova reproduza o mesmo total do PDF que ele já baixou.
        ...buildQuoteTotalsColumns(currentData),
        ...buildQuotePaymentColumns(currentData),
        ...buildQuotePdfTemplateColumns(currentData),
        title: currentData.title?.trim() || null,
        items: currentData.items.map((item) => ({
          id: item.id,
          product_id: null,
          name: item.name,
          description: item.description || null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0,
          discount_type: item.discount_type || 'value',
          image_url: item.image_url || null,
          // Fonte única: a conta daqui ignorava `discount_type`, então um item
          // com desconto percentual gravava no rascunho do visitante um total de
          // linha que não batia com o total do documento.
          total: quoteItemLineTotal(item),
          itemType: item.itemType || 'service',
          unit: item.unit || undefined,
        })),
        created_at: new Date().toISOString(),
      };

      localStorage.setItem(GUEST_QUOTE_KEY, JSON.stringify(guestQuote));
      // Quote is now saved as GUEST_QUOTE_KEY — the auto-save draft is redundant.
      // Clear it so that if the visitor logs in later, the DraftIndicator doesn't
      // treat the leftover draft as an unsaved in-progress quote.
      clearDraft();
      // Mark visitor quote as used (historical consumption)
      setVisitorQuoteUsed(true);
      await generatePDF();
      toast.success('PDF gerado e rascunho salvo no dispositivo.');

      // Redirect to view mode for guest users. Use `replace: true` so the back
      // button skips the editable /quotes/new entry — the visitor should not be
      // able to jump back into an edit form after saving.
      navigate('/quotes/new?view=true', { replace: true });
      return;
    }
  };

  /**
   * Duplicar: solta o vínculo com o documento já salvo e mantém o conteúdo, para
   * o próximo "Salvar" criar um documento NOVO em vez de atualizar o anterior.
   *
   * A numeração é limpa junto (`sequenceId`/`savedSequenceId`): o número é do
   * documento, não do formulário — deixá-lo aqui faria a cópia nascer exibindo o
   * número de um orçamento que já existe, e o usuário mandaria dois documentos
   * com a mesma identificação para clientes diferentes.
   */
  const handleDuplicate = () => {
    setSavedQuoteId(null);
    setSavedSequenceId(null);
    setFormData((prev) => ({ ...prev, sequenceId: undefined, status: 'pending' }));
    setIsFormDirty(true);
    toast.success('Cópia criada. Salve para gerar um novo documento.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (user && !canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    if (!formData.clientName.trim()) {
      toast.warning('Informe o nome do cliente');
      return;
    }
    if (formData.items.length === 0) {
      toast.warning('Adicione pelo menos um item ao orçamento');
      return;
    }
    if (formData.items.some((item) => !item.name.trim())) {
      toast.warning('Preencha o nome de todos os itens');
      return;
    }

    // Guest mode
    if (!user) {
      const existingQuote = localStorage.getItem(GUEST_QUOTE_KEY);
      if (existingQuote) {
        setShowSignUpModal(true);
        return;
      }

      const guestQuote: GuestQuote = {
        id: createUuid(),
        client_name: formData.clientName,
        status: formData.status || 'pending',
        document_type: formData.documentType || 'orcamento',
        validity_days: parseInt(formData.validityDays) || null,
        ...buildQuoteWarrantyColumns(formData),
        ...buildQuoteHeadingColumns(formData),
        ...buildQuoteExtraFieldsColumns(formData),
        notes: formData.notes || null,
        terms_conditions: formData.termsConditions || null,
        ...buildQuoteTotalsColumns(formData),
        ...buildQuotePaymentColumns(formData),
        ...buildQuotePdfTemplateColumns(formData),
        title: formData.title?.trim() || null,
        items: formData.items.map((item) => ({
          id: item.id,
          product_id: null,
          name: item.name,
          description: item.description || null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0,
          discount_type: item.discount_type || 'value',
          image_url: item.image_url || null,
          // Fonte única: a conta daqui ignorava `discount_type`, então um item
          // com desconto percentual gravava no rascunho do visitante um total de
          // linha que não batia com o total do documento.
          total: quoteItemLineTotal(item),
          itemType: item.itemType || 'service',
          unit: item.unit || undefined,
        })),
        created_at: new Date().toISOString(),
      };

      localStorage.setItem(GUEST_QUOTE_KEY, JSON.stringify(guestQuote));
      // Quote is now saved as GUEST_QUOTE_KEY — the auto-save draft is redundant.
      // Clear it so that if the visitor logs in later, the DraftIndicator doesn't
      // treat the leftover draft as an unsaved in-progress quote.
      clearDraft();
      // Mark visitor quote as used (historical consumption)
      setVisitorQuoteUsed(true);
      toast.success('Orçamento salvo! Crie uma conta para não perder.');

      // Redirect to view mode. Use `replace: true` so the back button skips the
      // editable /quotes/new entry — the visitor should not be able to jump back
      // into an edit form after saving.
      navigate('/quotes/new?view=true', { replace: true });
      return;
    }

    // Logged user - if already saved, just update
    if (savedQuoteId) {
      await updateExistingQuote(formDataRef.current);
      toast.success('Orçamento atualizado com sucesso!');
      return;
    }

    // Check subscription limit for new quotes only
    if (!canCreateQuote && !unlimitedQuotes.allowed) {
      unlimitedQuotes.require();
      return;
    }

    setSaving(true);

    // Snapshot inicial usando REF (sempre contém o estado mais recente, evita stale closure)
    const data: QuoteFormData = JSON.parse(JSON.stringify(formDataRef.current));

    const clientName = data.clientName.trim();
    const clientPhone = data.newClientPhone?.trim() || null;
    const clientAddress = resolveClientAddressLine(data);
    const clientDocument = onlyDigits(data.newClientDocument || '') || null;
    const items: QuoteItem[] = Array.isArray(data.items) ? data.items : [];
    const finalTotal = calculateTotal({ ...data, items });

    // Check if we're actually online
    const online = await checkOnline();

    // If offline, save to local queue and cache (shared with the offline
    // "Baixar PDF" path so both persist the quote identically).
    if (!online) {
      persistQuoteOffline(data);
      await incrementQuoteCount({
        offline: true,
        countTowardQuota: !unlimitedQuotes.allowed,
      });
      setSaving(false);
      clearDraft();
      navigate('/quotes', { replace: true });
      return;
    }

    let finalClientId: string | null = data.selectedClient?.id || null;
    let clientSaved = false;
    let catalogItemsSaved = 0;

    try {
      // Passo 1: Cliente (await explícito)
      if (data.saveNewClient && !data.selectedClient && clientName) {
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            user_id: user.id,
            company_id: activeCompany?.id ?? null,
            // Mesmo builder do outro save (ver comentário lá): a lista de colunas
            // de `clients` vive num lugar só.
            ...buildNewClientColumns(data),
          })
          .select('id')
          .maybeSingle();

        if (clientError || !newClient) {
          throw new Error(clientError?.message || 'Erro ao salvar cliente');
        }

        finalClientId = newClient.id;
        clientSaved = true;
      }

      // Passo 2: Itens no catálogo (await explícito em loop) - collect product IDs
      const catalogProductIds: Map<string, string> = new Map(); // itemId -> productId
      
      for (const item of items) {
        if (!item?.saveToCategory || !item.name?.trim()) continue;

        // Mesma porta única do outro save — ver `catalogFromQuoteItem.ts`.
        const outcome = await createCatalogProductFromQuoteItem({
          item,
          userId: user.id,
          companyId: activeCompany?.id ?? null,
        });

        if (!outcome.product) {
          console.error('Error saving item to catalog:', outcome.error);
          toast.warning(`"${item.name}" não foi salvo no catálogo: ${outcome.error}`);
          continue;
        }

        catalogProductIds.set(item.id, outcome.product.linkProductId);
        catalogItemsSaved += 1;

        // Cache the new catalog item for offline access (covers items created via quote flow)
        try {
          const cachedCatalog = (getCachedCatalog(user.id) || []) as any[];
          const next = [
            {
              id: outcome.product.productId,
              user_id: user.id,
              company_id: activeCompany?.id ?? null,
              name: item.name,
              price: item.unit_price,
              // Espelha o que acabou de ir para o banco — inclusive as colunas
              // de cadastro, que antes o cache jogava fora (a lista voltaria
              // sem categoria/código/estoque até o próximo refetch).
              cost: quoteItemUnitCost(item),
              ...catalogProductColumnsFromQuoteItem(item),
              image_url: item.image_url || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              _offline_pending: false,
            },
            ...cachedCatalog.filter((p) => p?.id !== outcome.product.productId),
          ];
          cacheCatalog(user.id, next);
        } catch (e) {
          console.warn('[NewQuotePage] Failed to cache new catalog item:', e);
        }
      }

      if (catalogItemsSaved > 0) {
        window.dispatchEvent(new CustomEvent('catalog-updated'));
      }

      // Get current lifetime_quotes_created from profile for persistent sequence ID
      const { data: profileData } = await supabase
        .from('profiles')
        .select('lifetime_quotes_created')
        .eq('id', user.id)
        .maybeSingle();

      // Next sequence ID = lifetime_quotes_created + 1 (before incrementing)
      const currentCount = profileData?.lifetime_quotes_created ?? 0;
      const nextSequenceId = currentCount + 1;

      // Passo 3: Orçamento (final)
      const { data: newQuote, error: quoteError } = await supabase
        .from('quotes')
        .insert({
          user_id: user.id,
          client_id: finalClientId,
          client_name: clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          company_id: activeCompany?.id || null, // Save the active company
          status: data.status,
          document_type: data.documentType || 'orcamento',
          validity_days: parseInt(data.validityDays) || null,
          ...buildQuoteWarrantyColumns(data),
          ...buildQuoteHeadingColumns(data),
          ...buildQuoteExtraFieldsColumns(data),
          notes: data.notes || null,
          terms_conditions: data.termsConditions || null,
          ...buildQuoteTotalsColumns({ ...data, items }),
          ...buildQuotePaymentColumns(data),
          ...buildQuotePdfTemplateColumns(data),
          title: data.title?.trim() || null,
          sequence_id: nextSequenceId,
          show_sequence_id: data.showSequenceId || false,
        })
        .select('id')
        .maybeSingle();

      if (quoteError || !newQuote) {
        throw new Error(quoteError?.message || 'Erro ao salvar orçamento');
      }

      // Store the saved quote ID to enable action buttons
      setSavedQuoteId(newQuote.id);
      
      // Lock the sequence ID to prevent display from changing to next number
      setSavedSequenceId(nextSequenceId);
      setFormData(prev => ({ ...prev, sequenceId: nextSequenceId }));

      const itemsToInsert = items.map((item, index) =>
        // Use the newly created product_id from catalog OR existing product_id
        buildQuoteItemInsert(item, index, newQuote.id, {
          productId: catalogProductIds.get(item.id),
        }),
      );

      const { error: itemsError } = await supabase.from('quote_items').insert(itemsToInsert);
      if (itemsError) {
        throw new Error(`Erro ao salvar itens do orçamento: ${itemsError.message}`);
      }

      // Insert general photos if any
      const generalPhotos = data.generalPhotos || [];
      if (generalPhotos.length > 0) {
        const photosToInsert = await Promise.all(
          generalPhotos.map(async (photo, index) => ({
            quote_id: newQuote.id,
            user_id: user.id,
            image_url: await ensureStorageUrl(photo.imageUrl, 'quote-photos', 'general', user.id),
            title: photo.title || null,
            description: photo.description || null,
            position: index,
          }))
        );

        const { error: photosError } = await supabase.from('quote_photos').insert(photosToInsert);
        if (photosError) {
          console.error('Error saving general photos:', photosError);
          throw new Error(`Erro ao salvar fotos do orçamento: ${photosError.message}`);
        }
      }

      window.dispatchEvent(new CustomEvent('quotes-updated'));
      await incrementQuoteCount({
        countTowardQuota: !unlimitedQuotes.allowed,
        sequenceId: nextSequenceId,
      });

      // Clear the draft after successful save
      clearDraft();

      if (catalogItemsSaved > 0 && clientSaved) {
        toast.success(
          `Orçamento salvo! Cliente e ${catalogItemsSaved} ${catalogItemsSaved === 1 ? 'item adicionado' : 'itens adicionados'} ao sistema.`
        );
      } else if (catalogItemsSaved > 0) {
        toast.success(
          `Orçamento salvo e ${catalogItemsSaved} ${catalogItemsSaved === 1 ? 'novo item adicionado' : 'novos itens adicionados'} ao catálogo!`
        );
      } else if (clientSaved) {
        toast.success('Orçamento salvo e cliente adicionado à lista!');
      } else {
        toast.success('Orçamento salvo com sucesso!');
      }

      // Redirect to edit page so user can continue editing with full functionality
      navigate(`/quotes/${newQuote.id}`, { replace: true });
    } catch (e) {
      console.error('Save quote error:', e);
      // A recusa da RLS chegava aqui como a mensagem CRUA do Postgres, em
      // inglês ("new row violates row-level security policy for table quotes") —
      // era o que a conta nova sem empresa lia ao tentar salvar (D14). Erro de
      // banco nunca é texto de interface.
      toast.error(
        describePersistError(e, {
          hasCompany: !!activeCompany,
          fallback: 'Erro ao salvar orçamento',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout showFab={false}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — sangrado, curva embaixo, TOTAL ao vivo                       */}
        {/* ------------------------------------------------------------------ */}
        {/* Número, switch do PDF e status vivem aqui (e por isso o formulário
            recebe `showIdentityFields={false}`): o cabeçalho é o lugar onde o
            prestador confere de relance o que está montando e por quanto. */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => {
                // Visitante em modo leitura chegou aqui pelo fluxo de criar-e-salvar;
                // voltar pelo histórico poderia cair em /quotes/new com o formulário
                // preenchido, deixando-o editar o que deveria estar travado.
                if (isGuestReadOnly) {
                  navigate('/dashboard');
                } else {
                  navigate(-1);
                }
              }}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">
                {isGuestReadOnly ? 'Modo visualização' : 'Novo documento'}
              </p>
              <h1 className="truncate text-lg font-bold leading-tight">
                {getDocumentTypeShortLabel(formData.documentType)}
              </h1>
            </div>
            {/* Duplicar: reaproveita o documento que está na tela como base de um
                NOVO. Só faz sentido depois de salvo — antes disso não há o que
                duplicar, e o próprio formulário já é o rascunho. */}
            {!isGuestReadOnly && !!savedQuoteId && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
                onClick={handleDuplicate}
                aria-label="Duplicar orçamento"
                title="Duplicar orçamento"
              >
                <Copy className="h-4.5 w-4.5" />
              </Button>
            )}
            {!!formData.sequenceId && (
              <div className="flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1">
                <Hash className="h-3.5 w-3.5 text-white/80" />
                <span className="font-mono text-sm font-bold leading-none">
                  {String(formData.sequenceId).padStart(4, '0')}
                </span>
              </div>
            )}
          </div>

          <div className="relative mt-5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-white/75">
                Total {documentTypeOfLabel(formData.documentType)}
              </p>
              <p
                className={cn(
                  'truncate font-bold leading-tight',
                  heroTotalSizeClass(formatCurrency(total)),
                )}
              >
                {formatCurrency(total)}
              </p>
              <p className="mt-0.5 text-xs text-white/70">
                {formData.items.length === 0
                  ? 'Nenhum item ainda'
                  : plural(formData.items.length, 'item', 'itens')}
              </p>
            </div>
            {/*
              DECISÃO PREMATURA: o switch só aparece DEPOIS de salvar.

              Num documento que ainda não existe, "Nº no PDF" é a primeira coisa
              que o hero oferece — e quase ninguém veio decidir isso. Pior: é um
              controle de aparência do PDF disputando o lugar mais nobre da tela
              com o total. Depois de salvo ele reaparece aqui mesmo, sem o
              usuário ter que procurar em outro lugar.
            */}
            {!!formData.sequenceId && !!savedQuoteId && !isGuestReadOnly && (
              /* Trilho ESCURO de propósito: o thumb do Switch é branco, e um
                 trilho claro sobre o hero verde deixaria o controle invisível. */
              <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-emerald-950/25 px-3 py-1.5">
                <span className="text-[11px] leading-none text-white">Nº no PDF</span>
                <Switch
                  checked={formData.showSequenceId || false}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, showSequenceId: checked }))
                  }
                  className="scale-90 data-[state=checked]:bg-emerald-900 data-[state=unchecked]:bg-emerald-950/60"
                />
              </label>
            )}
          </div>

          {/*
            Status: os 4 são um CONJUNTO e cabem na largura da tela — grade de 4
            colunas, não trilho rolável (que escondia "Cancelado" na borda).

            Só DEPOIS de salvar: um orçamento que ainda não existe nasce
            necessariamente "Pendente", então oferecer as quatro opções é pedir
            uma decisão que não existe — e pior, sugere que "Aprovado" é algo que
            o prestador marca antes de mandar para o cliente. Na tela de edição
            (QuoteDetailPage) elas aparecem desde o início, que é quando a
            escolha passa a ser real.
          */}
          {!isGuestReadOnly && !!savedQuoteId && (
            <div className="mt-4 grid grid-cols-4 gap-1.5">
              {QUOTE_STATUS_OPTIONS.map((option) => {
                const active = option.value === formData.status;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, status: option.value }))}
                    className={cn(
                      'flex min-w-0 items-center justify-center gap-1 rounded-full px-1 py-1.5 text-[11px] font-medium transition-all',
                      active
                        ? 'bg-white text-emerald-800 shadow-[inset_0_-3px_0_rgba(0,0,0,0.12)]'
                        : 'bg-white/15 text-white/85 hover:bg-white/25',
                    )}
                  >
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', option.dot)} />
                    <span className="truncate">{option.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Aviso de leitura para o visitante */}
        {isGuestReadOnly && (
          <Alert className="relative z-10 -mt-9 border-muted bg-card shadow-sm">
            <Lock className="h-4 w-4" />
            <AlertDescription>
              Modo visualização. Crie uma conta grátis para editar este orçamento.
            </AlertDescription>
          </Alert>
        )}

        {/* Formulário — o cartão do cliente sobe e encosta na curva do hero */}
        <div className={cn('relative z-10 space-y-5', !isGuestReadOnly && '-mt-9')}>
          <QuoteForm
            data={formData}
            onChange={setFormData}
            disabled={isGuestReadOnly}
            showSaveToCatalog={true}
            showTypeAndUnit={!user}
            showIdentityFields={false}
          />
        </div>

          {/* Buttons section */}
          {isGuestReadOnly ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button3D
                className="w-full sm:flex-1"
                onClick={handleDownloadPDF}
              >
                <FileDown className="mr-2 h-5 w-5" /> Baixar PDF
              </Button3D>
              <Button3D
                className="w-full sm:flex-1"
                onClick={() => setShowSignUpModal(true)}
              >
                <Edit className="mr-2 h-5 w-5" /> Editar orçamento
              </Button3D>
            </div>
          ) : (
            <>
              {/* Offline warning for Link/View (PDF and Save work offline) */}
              {!isOnline && (
                <Alert className="mt-6 border-amber-500/50 bg-amber-500/10">
                  <WifiOff className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    Sem internet: Link e Visualização indisponíveis. Você pode salvar o orçamento e baixar o PDF offline.
                  </AlertDescription>
                </Alert>
              )}

              {/*
                Barra FLUTUANTE acima da navegação inferior: com o formulário
                longo, o botão de salvar ficava a três rolagens de distância do
                que o usuário acabou de digitar.

                UMA linha só (ver QuoteActionBar): num documento que ainda não
                existe, a única ação que faz sentido é SALVAR. "Baixar PDF" é
                atalho (ele salva por dentro) e link/visualização nem existem
                ainda — os três moram no "⋯", e os dois indisponíveis levam o
                motivo junto em vez de virarem botões cinzas que não respondem.
              */}
              <QuoteActionBar
                primaryTourId="tour-quote-save"
                primary={{
                  id: 'salvar',
                  // "Salvar" (e não "Salvar orçamento"): o cabeçalho da tela já
                  // diz o que é, e o rótulo tem que ser IDÊNTICO ao da tela de
                  // edição — o mesmo gesto não pode ter dois nomes.
                  label: saving ? 'Salvando...' : savedQuoteId ? 'Salvo ✓' : 'Salvar',
                  icon: saving ? ActionSpinner : Save,
                  onClick: handleSave,
                  disabled: saving || !!savedQuoteId,
                }}
                more={[
                  {
                    id: 'pdf',
                    label: saving ? 'Gerando...' : 'Baixar PDF',
                    icon: FileDown,
                    onClick: handleDownloadPDF,
                    disabled: saving,
                  },
                  {
                    id: 'link',
                    label: 'Copiar link',
                    icon: Link,
                    onClick: () => {
                      if (!savedQuoteId) return;
                      navigator.clipboard.writeText(getPublicQuoteUrl(savedQuoteId));
                      toast.success('Link copiado!');
                    },
                    disabled: !savedQuoteId || !isOnline,
                    // Rede ANTES de "salve primeiro": sem conexão, salvar não
                    // desbloqueia nada — o orçamento vai para a fila com id
                    // temporário e link público não existe. Mandar salvar seria
                    // apontar um passo que não resolve.
                    disabledReason: !isOnline
                      ? 'Precisa de internet'
                      : 'Salve o orçamento primeiro',
                  },
                  {
                    id: 'visualizar',
                    label: saving ? 'Salvando...' : 'Ver como o cliente vê',
                    icon: Eye,
                    onClick: async () => {
                      if (!savedQuoteId) return;
                      // Auto-save before viewing
                      try {
                        setSaving(true);
                        await updateExistingQuote(formDataRef.current);
                      } catch (error) {
                        console.error('Error saving before preview:', error);
                      } finally {
                        setSaving(false);
                      }
                      window.open(`/o/${savedQuoteId}`, '_blank');
                    },
                    disabled: !savedQuoteId || saving || !isOnline,
                    // Rede ANTES de "salve primeiro": sem conexão, salvar não
                    // desbloqueia nada — o orçamento vai para a fila com id
                    // temporário e link público não existe. Mandar salvar seria
                    // apontar um passo que não resolve.
                    disabledReason: !isOnline
                      ? 'Precisa de internet'
                      : 'Salve o orçamento primeiro',
                  },
                ]}
              />
            </>
          )}
        </div>
      {/* Modals */}
      <SignUpModal open={showSignUpModal} onOpenChange={setShowSignUpModal} />
    </MainLayout>
  );
}
